// Report — the client-facing report as a LIVE page, Curro-branded: every
// figure is computed from the current published run (or the admin-pinned
// preview run) through /api/summary + /api/breakdown, and tenant users see it
// force-scoped to their slice. Charts are dependency-free SVG/CSS (Charts.jsx).

import { useEffect, useRef, useState } from "react";
import { AnalyticsApi, InsightsApi } from "../hooks/useApi.js";
import { useRun } from "../hooks/useRun.jsx";
import { useAuth } from "../auth/AuthProvider.jsx";
import { CountUp, Donut, HBars, Timeline } from "../components/Charts.jsx";
import TransactionsModal from "../components/TransactionsModal.jsx";
import AiSummary from "../components/AiSummary.jsx";
import InsightsPanels from "../components/InsightsPanels.jsx";
import { PrintContext } from "../components/PrintContext.jsx";
import Icon from "../components/Icon.jsx";
import { fmtR, fmtRm, fmtN } from "../lib/format.js";

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const monthName = (k) => {
  const m = /^(\d{4})-(\d{2})/.exec(String(k));
  return m ? `${MONTH_NAMES[Number(m[2]) - 1]} ${m[1]}` : String(k);
};
// A clicked month key → that month's [first, last] day for the drill filter.
// Handles both the API's "%b %Y" format ("Feb 2024") and ISO "2024-02".
const MONTHS3 = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const monthRange = (k) => {
  const s = String(k).trim();
  let y = null, mo = null;
  let m = /^([A-Za-z]{3})[A-Za-z]*\s+(\d{4})$/.exec(s);
  if (m) { mo = MONTHS3.indexOf(m[1].toLowerCase()); y = Number(m[2]); }
  else { m = /^(\d{4})-(\d{2})/.exec(s); if (m) { y = Number(m[1]); mo = Number(m[2]) - 1; } }
  if (y == null || mo == null || mo < 0) return null;
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(new Date(Date.UTC(y, mo, 1))), to: iso(new Date(Date.UTC(y, mo + 1, 0))) };
};

export default function Report() {
  const { runId } = useRun();
  const { user } = useAuth();
  // Tenants see the AI section only if granted; admin/client always.
  const canSeeAi = user?.role !== "tenant" || user?.canViewAiSummary === true;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [drill, setDrill] = useState(null);   // {filter, title} → TransactionsModal
  const [printing, setPrinting] = useState(false);   // true only during PDF export
  const [pdfBusy, setPdfBusy] = useState(false);
  const [aiGenerated, setAiGenerated] = useState(false);   // include AI summary in PDF only if generated
  const [aiText, setAiText] = useState(null);   // the generated markdown, forwarded to the server PDF
  const insightsRef = useRef(null);
  // ?print=1 renders the report in print layout WITHOUT calling window.print — a static
  // "print preview" used by the automated PDF test (headless Chrome page.pdf) and for debugging.
  const printPreview = typeof window !== "undefined" && new URLSearchParams(window.location.search).has("print");
  const isPrinting = printing || printPreview;
  useEffect(() => { if (printPreview) insightsRef.current?.ensureAllLoaded?.(); }, [printPreview]);

  // Server-side PDF (QuestPDF) — the preferred path. Calls the backend, which builds the
  // whole report chart-by-chart and streams a finished file. No theme flash, no page scroll,
  // no browser print dialog, no browser-injected date/URL header. The already-generated AI
  // summary (if any) is forwarded so it's included; it is never auto-generated for the PDF.
  const downloadPdf = async () => {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      const qs = runId ? `runId=${runId}` : "";
      const res = await fetch(`/api/wf/report-pdf${qs ? `?${qs}` : ""}`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ aiSummary: aiText || null }),
      });
      if (!res.ok) throw new Error(`PDF generation failed (HTTP ${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "westfert-sales-report.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      setError(e.body?.error ?? e.message);
    } finally {
      setPdfBusy(false);
    }
  };

  // LEGACY browser print-to-PDF path (kept as a fallback; no longer wired to the button).
  // Forces a light theme + static "print mode", scrolls to paint every chart, then window.print().
  // eslint-disable-next-line no-unused-vars
  const downloadPdfBrowser = async () => {
    if (pdfBusy) return;
    setPdfBusy(true);
    const html = document.documentElement;
    const prevTheme = html.getAttribute("data-theme");
    html.setAttribute("data-theme", "light");
    setPrinting(true);
    let done = false;
    const finish = () => {
      if (done) return; done = true;
      setPrinting(false);
      if (prevTheme) html.setAttribute("data-theme", prevTheme); else html.removeAttribute("data-theme");
      setPdfBusy(false);
    };
    try {
      // Load all insight tabs so §06 is complete. The AI summary is NOT auto-generated —
      // it's only included in the PDF if the user already generated it (section is hidden otherwise).
      await insightsRef.current?.ensureAllLoaded?.();
    } catch { /* print whatever loaded */ }
    // CRITICAL: charts gate on IntersectionObserver (render on scroll-in). Scroll the whole
    // report so every below-the-fold chart actually paints before printing — otherwise the
    // PDF shows blank chart cards. Then return to top.
    const step = 500;
    for (let y = 0; y <= document.body.scrollHeight; y += step) {
      window.scrollTo(0, y);
      await new Promise((r) => setTimeout(r, 60));
    }
    window.scrollTo(0, 0);
    await new Promise((res) => setTimeout(res, 500));
    window.addEventListener("afterprint", finish, { once: true });
    window.print();
  };

  useEffect(() => {
    setError(null);   // eslint-disable-line react-hooks/set-state-in-effect
    const filter = {};
    Promise.all([
      AnalyticsApi.summary(filter, runId),
      AnalyticsApi.breakdown("category", filter, runId),
      AnalyticsApi.breakdown("campus", filter, runId),
      AnalyticsApi.breakdown("source", filter, runId),
      AnalyticsApi.breakdown("month", filter, runId),
      AnalyticsApi.breakdown("vendor", filter, runId),
      AnalyticsApi.meta(runId),
      InsightsApi.rawMaterialMargin(filter, runId).catch(() => null),   // raw-material margins (Report section); null if unavailable
      InsightsApi.marginByClass(filter, runId).catch(() => null),       // recipe-vs-raw margin classes (Report section); null if unavailable
    ])
      .then(([summary, byCategory, byCampus, bySource, byMonth, byVendor, meta, rawMaterials, marginClasses]) =>
        setData({ summary, byCategory, byCampus, bySource, byMonth, byVendor, meta, rawMaterials, marginClasses }))
      .catch((e) => setError(e.body?.error ?? e.message));
  }, [runId]);

  if (error) return <div className="error"><Icon name="alert" size={16} /> {error}</div>;
  if (!data) return <p className="hint">Building the report from the published snapshot…</p>;

  const { summary, byCategory, byCampus, bySource, byMonth, byVendor, meta, rawMaterials, marginClasses } = data;

  // Drill: click a chart element → the raw invoice lines behind it (modal). Customer
  // charts show NAMES; the filter needs the CODE, so map name→code via meta.campuses.
  const codeByName = Object.fromEntries((meta.campuses ?? []).map((c) => [c.label, c.key]));
  const openDrill = (dim, value) => {
    if (value == null || value === "(none)" || value === "(Uncategorised)") return;
    let filter, title = value;
    if (dim === "category") filter = { categories: [value] };
    else if (dim === "campus") filter = { campuses: [codeByName[value] ?? value] };
    else if (dim === "source") filter = { sources: [value] };
    else if (dim === "vendor") filter = { products: [value] };
    else if (dim === "month") { const r = monthRange(value); if (!r) return; filter = { from: r.from, to: r.to }; title = monthName(value); }
    else return;
    setDrill({ filter, title: `Transactions · ${title}` });
  };

  // live insight figures (each guards against missing data)
  const uncategorised = byCategory.find((c) => c.key === "(none)" || /^uncat/i.test(c.key));
  const uncatPct = uncategorised && summary.netSpend ? (uncategorised.spend / summary.netSpend) * 100 : null;
  const topCampus = byCampus[0];
  const dated = byMonth.filter((m) => m.key !== "(none)");
  const peakMonth = dated.length ? dated.reduce((a, b) => (b.spend > a.spend ? b : a)) : null;
  const topVendor = byVendor[0];
  const top10Vendor = byVendor.slice(0, 10).reduce((s, r) => s + r.spend, 0);

  // Raw-material gross margins (dedicated section). rm is null if the profitability stream isn't published.
  const rm = rawMaterials?.total?.revenue != null ? rawMaterials : null;
  const rmMaterials = (rm?.materials ?? []).map((r) => ({ key: r.key, label: r.label ?? r.key, spend: r.margin, marginPct: r.margin_pct }));
  const rmTrend = (rm?.trend ?? []).map((r) => ({ key: r.key, spend: r.margin }));

  // Recipe/blends vs Raw materials vs Other — "where the gross margin is made" section.
  const mc = marginClasses?.total?.revenue != null ? marginClasses : null;
  const mcClasses = (mc?.classes ?? []).map((r) => ({
    key: r.key, label: r.label, spend: r.margin, marginPct: r.margin_pct,
    categories: (r.category_list ?? "").split("|").filter(Boolean),   // member categories, for drill
  }));
  const mcRecipe = (mc?.classes ?? []).find((c) => /recipe/i.test(c.key));
  const mcRaw = (mc?.classes ?? []).find((c) => /raw material/i.test(c.key));
  // Drill a class → the underlying invoice lines for its member categories (transactions modal).
  const drillClass = (key) => {
    const c = mcClasses.find((x) => x.key === key);
    if (!c || !c.categories.length) return;
    setDrill({ filter: { categories: c.categories }, title: `Transactions · ${c.label}` });
  };

  return (
    <PrintContext.Provider value={isPrinting}>
    <div className={`report rise${isPrinting ? " report-pdf" : ""}`}>
      {/* hero */}
      <div className="report-hero">
        <div className="deco" />
        <button type="button" className="btn ghost sm no-print" onClick={downloadPdf} disabled={pdfBusy}
          style={{ position: "absolute", top: 18, right: 18, zIndex: 2 }}>
          <Icon name="download" size={14} /> {pdfBusy ? "Preparing…" : "Download PDF"}
        </button>
        <div className="eyebrow">Sales{runId ? ` · Preview — snapshot #${runId}` : ""}</div>
        <h1>Sales &amp; Deliveries Analysis</h1>
        <p>A live read of {fmtN(summary.lines)} invoice lines across {meta.campuses.length} customers
          ({meta.dateMin} → {meta.dateMax}). All figures are revenue net of credits, computed from the published
          snapshot{runId ? ` (pinned run #${runId})` : ""} and reflect your access scope.</p>
        <div className="hero-kpis">
          <div className="hk"><div className="v"><CountUp value={summary.netSpend} fmt={fmtR} /></div><div className="l">Total revenue</div></div>
          <div className="hk"><div className="v"><CountUp value={summary.lines} fmt={fmtN} /></div><div className="l">Invoice lines</div></div>
          <div className="hk"><div className="v"><CountUp value={summary.avgLine} fmt={fmtR} /></div><div className="l">Avg line value</div></div>
          <div className="hk"><div className="v"><CountUp value={meta.campuses.length} fmt={fmtN} /></div><div className="l">Customers</div></div>
        </div>
      </div>

      {/* AI executive summary — on-demand, from aggregated (name-anonymised) figures.
          In a PDF export it's included ONLY if already generated (never auto-generated). */}
      {canSeeAi && (!isPrinting || aiGenerated) && (
        <section className="report-section">
          <div className="eyebrow">AI · executive summary</div>
          <AiSummary runId={runId} onGenerated={(text) => { setAiGenerated(true); setAiText(text ?? null); }} />
        </section>
      )}

      {/* Recipe vs raw materials — where the gross margin is made (management focus) */}
      {mc && (
        <section className="report-section">
          <div className="rs-keep">
            <div className="eyebrow">Recipe vs raw materials · gross margin</div>
            <h2 className="section-title" style={{ marginTop: 4 }}>Where the gross margin is made</h2>
            <p className="sub" style={{ marginTop: -4, marginBottom: 12 }}>
              The margin on blended recipe products versus raw materials sold as-is — on a real
              cost-of-goods basis. The gap shows where the value is added.
            </p>
            {mcRecipe && mcRaw && (
              <div className="callout" style={{ marginBottom: 16 }}>
                <div className="big">{mcRecipe.margin_pct}%</div>
                <div className="txt">Blended <b>recipe products</b> earn <b>{mcRecipe.margin_pct}%</b> ({fmtR(mcRecipe.margin)}) —
                  versus just <b>{mcRaw.margin_pct}%</b> ({fmtR(mcRaw.margin)}) on <b>raw materials</b>.
                  The margin is made in the blending, not the raw materials.</div>
              </div>
            )}
            <div className="viz-grid two">
              <div className="viz-card">
                <h3>Gross margin by class</h3>
                <div className="sub">Amount · margin % · click to drill</div>
                <HBars rows={mcClasses} topN={3} subPct colorByIndex onSelect={drillClass} />
              </div>
              <div className="viz-card">
                <h3>Share of gross margin</h3>
                <div className="sub">Where the {fmtR(mc.total.margin)} of margin comes from</div>
                <Donut rows={mcClasses} topN={3} centerTop={fmtRm(mc.total.margin)} centerSub="gross margin" onSelect={drillClass} />
              </div>
            </div>
            <div className="viz-card" style={{ marginTop: 16 }}>
              <h3>By class</h3>
              <div className="sub">Revenue · gross margin · margin %</div>
              <div className="tbl-wrap">
                <table className="m-cards">
                  <thead><tr><th>Class</th><th className="num">Revenue</th><th className="num">Gross margin</th><th className="num">Margin %</th></tr></thead>
                  <tbody>
                    {(mc.classes ?? []).map((c) => (
                      <tr key={c.key} onClick={() => drillClass(c.key)} style={{ cursor: "pointer" }} title={`Transactions · ${c.label}`}>
                        <td>{c.label}</td>
                        <td className="num" data-label="Revenue">{fmtR(c.revenue)}</td>
                        <td className="num" data-label="Gross margin">{fmtR(c.margin)}</td>
                        <td className={`num ${c.margin_pct < 0 ? "neg" : ""}`} data-label="Margin %">{c.margin_pct}%</td>
                      </tr>
                    ))}
                    <tr className="total">
                      <td>Total</td>
                      <td className="num" data-label="Revenue">{fmtR(mc.total.revenue)}</td>
                      <td className="num" data-label="Gross margin">{fmtR(mc.total.margin)}</td>
                      <td className="num" data-label="Margin %">{mc.total.margin_pct}%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Raw materials — gross margin (management focus) */}
      {rm && (
        <section className="report-section">
          <div className="rs-keep">
            <div className="eyebrow">Raw materials · gross margin</div>
            <h2 className="section-title" style={{ marginTop: 4 }}>Gross margin on raw materials</h2>
            <p className="sub" style={{ marginTop: -4, marginBottom: 12 }}>
              Fertiliser, Kalk &amp; Sweepings raw materials sold as products, on a real cost-of-goods basis.
              (Raw materials blended into finished product realise their margin on the blend, not here.)
            </p>
            <div className="callout" style={{ marginBottom: 16 }}>
              <div className="big">{rm.total.margin_pct}%</div>
              <div className="txt"><b>{fmtR(rm.total.margin)}</b> gross margin on <b>{fmtR(rm.total.revenue)}</b> of
                raw-material sales — the thin-margin core the management team watches closely.</div>
            </div>
            <div className="viz-grid two">
              <div className="viz-card">
                <h3>Margin by raw material</h3>
                <div className="sub">Top materials · amount · margin %</div>
                <HBars rows={rmMaterials} topN={10} expandable step={10} subPct colorByIndex onSelect={(v) => openDrill("vendor", v)} />
              </div>
              <div className="viz-card">
                <h3>By category</h3>
                <div className="sub">Revenue · gross margin · margin %</div>
                <div className="tbl-wrap">
                  <table className="m-cards">
                    <thead><tr><th>Category</th><th className="num">Revenue</th><th className="num">Gross margin</th><th className="num">Margin %</th></tr></thead>
                    <tbody>
                      {(rm.categories ?? []).map((c) => (
                        <tr key={c.key} onClick={() => openDrill("category", c.key)} style={{ cursor: "pointer" }} title={`Transactions · ${c.label}`}>
                          <td>{c.label}</td>
                          <td className="num" data-label="Revenue">{fmtR(c.revenue)}</td>
                          <td className="num" data-label="Gross margin">{fmtR(c.margin)}</td>
                          <td className={`num ${c.margin < 0 ? "neg" : ""}`} data-label="Margin %">{c.margin_pct}%</td>
                        </tr>
                      ))}
                      <tr className="total">
                        <td>Total</td>
                        <td className="num" data-label="Revenue">{fmtR(rm.total.revenue)}</td>
                        <td className="num" data-label="Gross margin">{fmtR(rm.total.margin)}</td>
                        <td className="num" data-label="Margin %">{rm.total.margin_pct}%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            {rmTrend.length > 1 && (
              <div className="viz-card" style={{ marginTop: 16 }}>
                <h3>Raw-material gross margin by month</h3>
                <div className="sub">Margin (R) across the period</div>
                <Timeline rows={rmTrend} onSelect={(v) => openDrill("month", v)} />
              </div>
            )}
          </div>
        </section>
      )}

      {/* 01 — where money goes */}
      <section className="report-section">
        <div className="rs-keep">
          <div className="eyebrow">01 · Where the revenue comes from</div>
          <h2 className="section-title" style={{ marginTop: 4 }}>Revenue by product category</h2>
          <p className="sub no-print" style={{ marginTop: -4, marginBottom: 12 }}>Tip: click any bar, slice or month to see the underlying transactions.</p>
          <div className="viz-grid two">
            <div className="viz-card">
              <h3>Top categories</h3>
              <div className="sub">Revenue, largest first</div>
              <HBars rows={byCategory} topN={8} onSelect={(v) => openDrill("category", v)} />
            </div>
            <div className="viz-card">
              <h3>Share of total</h3>
              <div className="sub">Top 8 + others</div>
              <Donut rows={byCategory} topN={8} centerTop={fmtRm(summary.netSpend)} centerSub="total revenue" onSelect={(v) => openDrill("category", v)} />
            </div>
          </div>
        </div>
        {uncategorised && (
          <div className="callout" style={{ marginTop: 20 }}>
            <div className="big"><CountUp value={uncatPct} fmt={(v) => v.toFixed(1) + "%"} /></div>
            <div className="txt"><b>{fmtR(uncategorised.spend)}</b> of revenue is <b>uncategorised</b> —
              classifying these product lines would sharpen every view here.</div>
          </div>
        )}
      </section>

      {/* 02 — benchmarking */}
      <section className="report-section">
        <div className="rs-keep">
          <div className="eyebrow">02 · Benchmarking</div>
          <h2 className="section-title" style={{ marginTop: 4 }}>Customer comparison</h2>
          <div className="viz-grid two">
            <div className="viz-card">
              <h3>Revenue by customer</h3>
              <div className="sub">{meta.campuses.length} customers, largest first</div>
              <HBars rows={byCampus} topN={12} onSelect={(v) => openDrill("campus", v)} />
            </div>
            <div className="viz-card">
              <h3>Share of revenue by customer</h3>
              <div className="sub">Top customers + others</div>
              <Donut rows={byCampus} topN={8} centerTop={String(meta.campuses.length)} centerSub="customers" onSelect={(v) => openDrill("campus", v)} />
            </div>
          </div>
        </div>
        {topCampus && summary.netSpend > 0 && (
          <div className="callout" style={{ marginTop: 20 }}>
            <div className="big">{((topCampus.spend / summary.netSpend) * 100).toFixed(1)}%</div>
            <div className="txt"><b>{topCampus.label ?? topCampus.key}</b> is the largest customer by revenue —
              {" "}{fmtR(topCampus.spend)} across {fmtN(topCampus.lines)} lines.</div>
          </div>
        )}
      </section>

      {/* 03 — process & control */}
      <section className="report-section">
        <div className="rs-keep">
          <div className="eyebrow">03 · Sales team</div>
          <h2 className="section-title" style={{ marginTop: 4 }}>Revenue by salesperson</h2>
          <div className="viz-grid two">
            <div className="viz-card">
              <h3>Salesperson mix</h3>
              <div className="sub">Share of revenue by rep</div>
              <Donut rows={bySource} topN={7} centerTop={String(bySource.length)} centerSub="salespeople" onSelect={(v) => openDrill("source", v)} />
            </div>
            <div className="viz-card">
              <h3>Revenue per salesperson</h3>
              <div className="sub">Largest first</div>
              <HBars rows={bySource} topN={9} colorByIndex onSelect={(v) => openDrill("source", v)} />
            </div>
          </div>
        </div>
      </section>

      {/* 04 — seasonality */}
      <section className="report-section">
        <div className="rs-keep">
          <div className="eyebrow">04 · Seasonality</div>
          <h2 className="section-title" style={{ marginTop: 4 }}>Monthly sales trend</h2>
          <div className="viz-card">
            <h3>Revenue by month <span className="hint" style={{ fontWeight: 400 }}>· R millions</span></h3>
            <div className="sub">Lines without a date excluded</div>
            <Timeline rows={byMonth} onSelect={(v) => openDrill("month", v)} />
          </div>
        </div>
        {peakMonth && (
          <div className="callout" style={{ marginTop: 20 }}>
            <div className="big">{fmtRm(peakMonth.spend)}</div>
            <div className="txt"><b>{monthName(peakMonth.key)}</b> is the peak month at {fmtR(peakMonth.spend)} across
              {" "}{fmtN(peakMonth.lines)} lines. Plan cash and approvals around it.</div>
          </div>
        )}
      </section>

      {/* 05 — supply base */}
      <section className="report-section">
        <div className="rs-keep">
        <div className="eyebrow">05 · Products</div>
        <h2 className="section-title" style={{ marginTop: 4 }}>Product concentration</h2>
        <div className="viz-grid two">
          {topVendor && (
            <div className="viz-card">
              <h3>Concentration</h3>
              <div className="sub">Top product vs the rest of the range</div>
              <Donut
                rows={[
                  { key: topVendor.key, spend: topVendor.spend },
                  { key: "Next 9 products", spend: Math.max(0, top10Vendor - topVendor.spend) },
                  { key: "All other products", spend: Math.max(0, summary.netSpend - top10Vendor) },
                ]}
                topN={3}
                palette={["#138A6E", "#71B52B", "#9DA790"]}
                centerTop={String(byVendor.length)} centerSub="products" />
            </div>
          )}
          <div className="viz-card">
            <h3>Top products</h3>
            <div className="sub">By revenue</div>
            <HBars rows={byVendor} topN={10} onSelect={(v) => openDrill("vendor", v)} />
          </div>
        </div>
        </div>
        {topVendor && summary.netSpend > 0 && (
          <div className="callout" style={{ marginTop: 20 }}>
            <div className="big">{((topVendor.spend / summary.netSpend) * 100).toFixed(1)}%</div>
            <div className="txt"><b>{topVendor.key}</b> alone is {fmtR(topVendor.spend)} —
              {" "}{((topVendor.spend / summary.netSpend) * 100).toFixed(1)}% of all revenue across just
              {" "}{fmtN(topVendor.lines)} lines. A key concentration to monitor.</div>
          </div>
        )}
      </section>

      {/* 06 — deeper insights (value-added, cross-stream, time-series) */}
      <section className="report-section">
        <div className="eyebrow">06 · Deeper insights</div>
        <h2 className="section-title" style={{ marginTop: 4 }}>Trends, fulfilment, customers &amp; cash</h2>
        <p className="sub" style={{ marginBottom: 16 }}>
          Time-series and cross-stream analytics across sales, orders, deliveries, commissions and recipes.
        </p>
        <InsightsPanels ref={insightsRef} runId={runId} />
      </section>

      <footer className="report-foot">
        <Icon name="shield" size={15} />
        Generated live from the published snapshot · figures reflect your access scope · The Media Crowd for Westfert
      </footer>

      {drill && (
        <TransactionsModal filter={drill.filter} runId={runId} title={drill.title} onClose={() => setDrill(null)} />
      )}
    </div>
    </PrintContext.Provider>
  );
}
